import {
  type ApolloClient,
  gql,
  type NormalizedCacheObject,
  useApolloClient,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client';
import { Close, Edit } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { type FormikProps, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { flattenDeep, get } from 'lodash';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  type SuperViewResponse as ApiSuperViewResponse,
  type ParsedSuperViewData,
  parseSuperView,
  type ValueData,
} from 'src/api/viewModels/data';
import FieldInput from 'src/components/FieldInput/FieldInput';
import { VIEW_GALLERY_CARD_META, VIEW_GALLERY_PAGE } from 'src/constants';
import { updateCollection } from 'src/dynamoQueries/collections';
import { createUpdateMetadataMutation } from 'src/dynamoQueries/metadataField';
import { buildSuperViewEditQuery } from 'src/dynamoQueries/superView';
import { DATE_FIELD_TYPES, formatDateDisplay, normalizeDateValue } from 'src/helpers/dateFields';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';

interface SuperViewField extends ParsedSuperViewData {
  slug: string;
  metadataFieldId?: string;
  fieldDefId: string;
  fieldType: string;
  label?: string;
  isEditable?: boolean;
  choices?: string[];
}

interface GalleryEditFormValues {
  [key: string]: string | string[] | undefined | null;
}

const PANEL_WIDTH = 380;

const useStyles = makeStyles((theme: Theme) => ({
  panel: {
    width: PANEL_WIDTH,
    backgroundColor: theme.palette.background.navbar,
    borderLeft: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    right: 0,
    top: 51,
    bottom: 0,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease',
    zIndex: 1100,
    '&.open': {
      transform: 'translateX(0)',
    },
  },
  panelHeader: {
    padding: theme.spacing(2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  panelHeaderTitle: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(0.5),
  },
  panelHeaderName: {
    fontSize: 18,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  panelContent: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2.5),
  },
  panelSection: {
    marginBottom: theme.spacing(3),
  },
  panelSectionTitle: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  metadataField: {
    marginBottom: theme.spacing(2),
  },
  metadataLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  metadataValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  descriptionField: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    minHeight: 60,
  },
  descriptionEmpty: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  panelActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
  },
  editIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.25),
    backgroundColor: `${theme.palette.secondary.main}26`, // 15% opacity
    color: theme.palette.secondary.main,
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  closeButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  headerIconButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  editFieldsSection: {
    '& .MuiFormControl-root': {
      display: 'block',
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    '& .MuiInputBase-root': {
      width: '100%',
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.action.disabled,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.text.secondary,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
  },
}));

const makeInitialValues = (superViewData: SuperViewField[]): GalleryEditFormValues => {
  const vals: GalleryEditFormValues = {};
  const parsedData = superViewData || [];

  parsedData.forEach((data) => {
    if (data.fieldType === 'MS' || data.fieldType === 'CB') {
      vals[data.slug] = data.value
        ? data.value.map((item: ValueData) => get(item, 'data')).filter((v): v is string => Boolean(v))
        : [];
    } else if (DATE_FIELD_TYPES.includes(data.fieldType as (typeof DATE_FIELD_TYPES)[number])) {
      const rawDateValue = data.value?.find((item) => Boolean(item?.data))?.data ?? data.show;
      vals[data.slug] = normalizeDateValue(rawDateValue, data.fieldType);
    } else {
      vals[data.slug] = data.show ?? '';
    }
  });

  return vals;
};

interface GalleryInfoPanelProps {
  isOpen: boolean;
  onClose: () => void;
  galleryId: string;
  galleryName: string;
  owner: string;
  totalItems: number;
  dateCreated?: string;
  lastModified?: string;
  canEdit?: boolean;
  onUpdateComplete?: () => void;
}

const GalleryInfoPanel = ({
  isOpen,
  onClose,
  galleryId,
  galleryName,
  owner,
  totalItems,
  dateCreated,
  lastModified,
  canEdit = true,
  onUpdateComplete,
}: GalleryInfoPanelProps): React.JSX.Element => {
  const classes = useStyles();
  const { toastr } = useCustomToast();
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const viewState = useReactiveVar(viewStateVar);
  const [isSaving, setIsSaving] = useState(false);

  // Use global state for edit mode
  const isEditing = viewState.isGalleryPanelEditing;

  const setIsEditing = (editing: boolean) => {
    viewStateVar({
      ...viewStateVar(),
      isGalleryPanelEditing: editing,
    });
  };

  // Reset edit mode on unmount (e.g., navigating away)
  useEffect(() => {
    return () => {
      viewStateVar({
        ...viewStateVar(),
        isGalleryPanelEditing: false,
      });
    };
  }, []);

  // Handle panel close - also exit edit mode
  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const {
    data: superViewResponse,
    loading: superViewLoading,
    error: superViewError,
  } = useQuery<ApiSuperViewResponse>(buildSuperViewEditQuery(), {
    skip: !galleryId,
    variables: {
      globalId: galleryId,
      viewComponentName: VIEW_GALLERY_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const superViewData = useMemo((): SuperViewField[] => {
    const parsed = parseSuperView(superViewResponse ?? null) || [];
    return parsed as SuperViewField[];
  }, [superViewResponse]);

  const buildMutationQuery = () => {
    const mutate = updateCollection(true);
    return gql(jsonToGraphQLQuery(mutate, { pretty: true }));
  };

  const buildMetadataMutation = () => {
    const mutate = createUpdateMetadataMutation(true);
    return gql(jsonToGraphQLQuery(mutate, { pretty: true }));
  };

  const [updateCollectionMutation] = useMutation(buildMutationQuery());
  const [updateMetadataMutation] = useMutation(buildMetadataMutation());

  const initialFormValues = useMemo((): GalleryEditFormValues => {
    return makeInitialValues(superViewData);
  }, [superViewData]);

  const formik: FormikProps<GalleryEditFormValues> = useFormik<GalleryEditFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSaving(true);

      try {
        await updateCollectionMutation({
          variables: {
            collectionId: galleryId,
            name: values.name,
          },
        });

        for (const field of superViewData) {
          const { slug, metadataFieldId, fieldDefId } = field;
          const fieldValue = values[slug];

          const variables: {
            globalId: string;
            fieldDefId: string;
            valuesList: string[];
            fieldId?: string;
          } = {
            globalId: galleryId,
            fieldDefId,
            // biome-ignore lint/suspicious/noExplicitAny: flattenDeep needs flexible input type for nested arrays/values from form fields
            valuesList: flattenDeep([fieldValue] as any).filter(
              (v: unknown): v is string => typeof v === 'string' && v !== '',
            ),
          };

          if (metadataFieldId) {
            variables.fieldId = metadataFieldId;
          }

          await updateMetadataMutation({ variables });
        }

        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'superView',
          args: {
            globalId: galleryId,
            viewComponentName: VIEW_GALLERY_CARD_META,
          },
        });

        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'superView',
          args: {
            globalId: galleryId,
            viewComponentName: VIEW_GALLERY_PAGE,
          },
        });

        client.cache.gc();

        toastr.success('Updated Gallery Successfully');
        setIsEditing(false);

        if (typeof onUpdateComplete === 'function') {
          onUpdateComplete();
        }
      } catch (err) {
        const error = err as Error;
        toastr.error(`Error updating gallery: ${error?.message}`);
      } finally {
        setIsSaving(false);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    setIsEditing(false);
  };

  const coreFields = superViewData.filter((field) => ['name', 'media_group'].includes(field.slug));
  const descriptionField = superViewData.find((field) => field.slug === 'description');
  const customFields = superViewData.filter(
    (field) => !['name', 'media_group', 'description', 'owner', 'created_date', 'modified_date'].includes(field.slug),
  );

  const getFieldDisplayValue = (field: SuperViewField): string => {
    if (field.fieldType === 'MS' || field.fieldType === 'CB') {
      const values = field.value?.map((item: ValueData) => get(item, 'data')).filter(Boolean) || [];
      return values.join(', ') || '-';
    }
    return field.show || '-';
  };

  // Handle superView error in useEffect to avoid side effects in render
  useEffect(() => {
    if (superViewError) {
      toastr.error(`Error loading gallery data: ${superViewError?.message}`);
    }
  }, [superViewError, toastr]);

  return (
    <Box className={`${classes.panel} ${isOpen ? 'open' : ''}`}>
      <Box className={classes.panelHeader}>
        <Box>
          <Typography className={classes.panelHeaderTitle}>Gallery Information</Typography>
          <Typography className={classes.panelHeaderName}>{galleryName}</Typography>
        </Box>
        <Box className={classes.headerActions}>
          {!isEditing && canEdit && (
            <IconButton
              size="small"
              onClick={() => setIsEditing(true)}
              className={classes.headerIconButton}
              title="Edit gallery"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={handleClose} className={classes.closeButton} title="Close panel">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className={classes.panelContent}>
        {superViewLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={32} color="secondary" />
          </Box>
        ) : isEditing ? (
          <>
            <Box className={classes.editIndicator}>
              <Edit fontSize="inherit" />
              Editing
            </Box>

            <Box className={classes.panelSection}>
              <Typography className={classes.panelSectionTitle}>Core Information</Typography>
              <Box className={classes.editFieldsSection}>
                {coreFields.map((field, index) => (
                  // biome-ignore lint/suspicious/noExplicitAny: FieldInput expects Field type with specific structure
                  <FieldInput key={field.slug || index} field={field as any} formik={formik as any} />
                ))}
              </Box>
            </Box>

            {descriptionField && (
              <Box className={classes.panelSection}>
                <Typography className={classes.panelSectionTitle}>Description</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={formik.values[descriptionField.slug] || ''}
                  onChange={(e) => formik.setFieldValue(descriptionField.slug, e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}

            {customFields.length > 0 && (
              <Box className={classes.panelSection}>
                <Typography className={classes.panelSectionTitle}>Custom Fields</Typography>
                <Box className={classes.editFieldsSection}>
                  {customFields.map((field, index) => (
                    // biome-ignore lint/suspicious/noExplicitAny: FieldInput expects Field type with specific structure
                    <FieldInput key={field.slug || index} field={field as any} formik={formik as any} />
                  ))}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box className={classes.panelSection}>
              <Typography className={classes.panelSectionTitle}>Core Information</Typography>
              {coreFields.map((field) => (
                <Box key={field.slug} className={classes.metadataField}>
                  <Typography className={classes.metadataLabel}>{field.label || field.slug}</Typography>
                  <Typography className={classes.metadataValue}>
                    {getFieldDisplayValue(field)}
                    {field.fallbackMessage && (
                      <Tooltip title={field.fallbackMessage} arrow>
                        <WarningAmberIcon
                          sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, verticalAlign: 'middle' }}
                        />
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
              ))}
              <Box className={classes.metadataField}>
                <Typography className={classes.metadataLabel}>Total Items</Typography>
                <Typography className={classes.metadataValue}>{totalItems} assets</Typography>
              </Box>
            </Box>

            <Box className={classes.panelSection}>
              <Typography className={classes.panelSectionTitle}>Description</Typography>
              <Box className={`${classes.descriptionField} ${!descriptionField?.show ? classes.descriptionEmpty : ''}`}>
                {descriptionField?.show || 'No description'}
              </Box>
            </Box>

            <Box className={classes.panelSection}>
              <Typography className={classes.panelSectionTitle}>Ownership</Typography>
              <Box className={classes.metadataField}>
                <Typography className={classes.metadataLabel}>Created By</Typography>
                <Typography className={classes.metadataValue}>{owner || '-'}</Typography>
              </Box>
              {dateCreated && (
                <Box className={classes.metadataField}>
                  <Typography className={classes.metadataLabel}>Date Created</Typography>
                  <Typography className={classes.metadataValue}>{formatDateDisplay(dateCreated)}</Typography>
                </Box>
              )}
              {lastModified && (
                <Box className={classes.metadataField}>
                  <Typography className={classes.metadataLabel}>Last Modified</Typography>
                  <Typography className={classes.metadataValue}>{formatDateDisplay(lastModified)}</Typography>
                </Box>
              )}
            </Box>

            {customFields.length > 0 && (
              <Box className={classes.panelSection}>
                <Typography className={classes.panelSectionTitle}>Custom Fields</Typography>
                {customFields.map((field) => (
                  <Box key={field.slug} className={classes.metadataField}>
                    <Typography className={classes.metadataLabel}>{field.label || field.slug}</Typography>
                    <Typography className={classes.metadataValue}>
                      {getFieldDisplayValue(field)}
                      {field.fallbackMessage && (
                        <Tooltip title={field.fallbackMessage} arrow>
                          <WarningAmberIcon
                            sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, verticalAlign: 'middle' }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {isEditing && (
        <Box className={classes.panelActions}>
          <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
            disabled={isSaving}
            sx={{ flex: 1 }}
          >
            {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GalleryInfoPanel;
